import { GLYPHS, MASCATE_ITEM, PING_KINDS, PING_LABELS, structureName } from "@eldrun/content";
import type { MapMarker, MarkerKind } from "./map-model.ts";

/**
 * Mapa com lista e detalhe (F82, mockup `eldrun-ui-g.png`): a lista "Pontos" filtrada pela forma, o
 * detalhe do ponto escolhido, a barra de escala e o toque no mapa. Projeções puras, sem DOM.
 */

export type MapFilter = "todos" | "pings" | "lugares" | "chefes" | "grupo";

/** Abas do filtro: o símbolo é a forma do grupo (o mockup filtra por forma: ! ◆ ★). */
export const MAP_FILTERS: readonly { id: MapFilter; label: string; symbol: string }[] = [
  { id: "todos", label: "Todos", symbol: "" },
  { id: "pings", label: "Pings", symbol: "!" },
  { id: "lugares", label: "Lugares", symbol: "◆" },
  { id: "chefes", label: "Chefes e Guardiões", symbol: "★" },
  { id: "grupo", label: "Grupo", symbol: GLYPHS.hexagono.char },
];

const CATEGORY: Record<MarkerKind, Exclude<MapFilter, "todos">> = {
  eu: "grupo",
  aliado: "grupo",
  ping: "pings",
  carranca: "lugares",
  pedra: "lugares",
  mascate: "lugares",
  jangada: "lugares",
  cruz: "lugares",
  redemoinho: "lugares",
  guardiao: "chefes",
  chefe: "chefes",
};

/** Tipo e a frase do detalhe de cada marcador (o que o ponto é no jogo). */
const KIND_INFO: Record<MarkerKind, { kind: string; text: string }> = {
  eu: { kind: "Você", text: "A seta aponta para onde você olha." },
  aliado: { kind: "Aliado", text: "Do grupo, com o número do slot. Caído, um ✕." },
  ping: { kind: "Ping", text: "Some em 6 s; cada jogador tem até 3 ativos." },
  carranca: { kind: "Lugar", text: "Aqui se paga a volta de quem caiu." },
  pedra: { kind: "Lugar", text: "Ao toque, desperta um Desafio; vencido, dá uma Bênção." },
  mascate: { kind: "Lugar", text: "Mercador: compra e vende por moedas." },
  jangada: { kind: "Lugar", text: "O Naufrágio: consertado, leva o grupo embora da ilha." },
  cruz: { kind: "Lugar", text: "Um aliado espera aqui; de dia, dá para revivê-lo." },
  redemoinho: { kind: "Perigo", text: "Redemoinho do Diabrete: a Garrafa Encantada o captura." },
  guardiao: { kind: "Guardião", text: "Santuário de um Guardião e da sua Gema." },
  chefe: { kind: "Chefe", text: "Chefe vivo na ilha." },
};

export interface MapRow {
  id: string;
  symbol: string;
  color: string;
  label: string;
  kind: string;
  /** Metros até você (inteiro), ou `undefined` sem você no mapa. */
  meters: number | undefined;
  distance: string;
}

/** Distância para ler: "35 m" ou "1,2 km". */
export function distanceText(meters: number | undefined): string {
  if (meters === undefined || !Number.isFinite(meters)) return "—";
  if (meters < 1000) return `${Math.round(meters)} m`;
  return `${(meters / 1000).toFixed(1).replace(".", ",")} km`;
}

const CARDINALS = ["norte", "nordeste", "leste", "sudeste", "sul", "sudoeste", "oeste", "noroeste"] as const;

/** Rumo de `from` até `to` em 8 direções; −z é o norte (o alto do mapa). */
export function cardinal(from: { x: number; z: number }, to: { x: number; z: number }): string {
  const dx = to.x - from.x;
  const dz = to.z - from.z;
  if (Math.hypot(dx, dz) < 0.5) return "aqui";
  const angle = Math.atan2(dx, -dz);
  return CARDINALS[(((Math.round(angle / (Math.PI / 4)) % 8) + 8) % 8)]!;
}

const metersTo = (self: { x: number; z: number } | undefined, m: { x: number; z: number }) => (self ? Math.hypot(m.x - self.x, m.z - self.z) : undefined);

/**
 * A lista "Pontos": os marcadores do filtro (menos você), do mais perto ao mais longe, e quantos há em
 * cada aba. Sem você no mapa (morto, espectador), por nome.
 */
export function mapList(markers: readonly MapMarker[], self: { x: number; z: number } | undefined, filter: MapFilter): { rows: MapRow[]; counts: Record<MapFilter, number> } {
  const counts: Record<MapFilter, number> = { todos: 0, pings: 0, lugares: 0, chefes: 0, grupo: 0 };
  const rows: MapRow[] = [];
  for (const m of markers) {
    if (m.kind === "eu") continue;
    const category = CATEGORY[m.kind];
    counts.todos++;
    counts[category]++;
    if (filter !== "todos" && filter !== category) continue;
    const meters = metersTo(self, m);
    rows.push({ id: m.id, symbol: m.symbol, color: m.color, label: m.label, kind: KIND_INFO[m.kind].kind, meters: meters === undefined ? undefined : Math.round(meters), distance: distanceText(meters) });
  }
  rows.sort((a, b) => (a.meters ?? 0) - (b.meters ?? 0) || a.label.localeCompare(b.label, "pt-BR"));
  return { rows, counts };
}

export interface MapDetail {
  id: string;
  symbol: string;
  color: string;
  title: string;
  kind: string;
  text: string;
  rows: [string, string][];
}

/** O detalhe do ponto escolhido: nome, tipo, distância, rumo, quem pingou e a posição. */
export function markerDetail(marker: MapMarker | undefined, self: { x: number; z: number } | undefined): MapDetail | undefined {
  if (!marker) return undefined;
  const info = KIND_INFO[marker.kind];
  const rows: [string, string][] = [["Tipo", marker.note ? `${info.kind} · ${marker.note}` : info.kind]];
  if (marker.by) rows.push(["Marcado por", marker.by]);
  if (marker.kind !== "eu") {
    rows.push(["Distância", distanceText(metersTo(self, marker))]);
    if (self) rows.push(["Direção", cardinal(self, marker)]);
  }
  rows.push(["Posição", `${Math.round(marker.x)}, ${Math.round(marker.z)}`]);
  return { id: marker.id, symbol: marker.symbol, color: marker.color, title: marker.by && marker.note ? marker.note : marker.label, kind: info.kind, text: info.text, rows };
}

const NICE_METERS = [10, 20, 50, 100, 200, 500, 1000, 2000] as const;

/** Barra de escala: o maior valor redondo que cabe em `maxPx` (pelo menos o menor), em pixels de tela. */
export function scaleBar(pxPerMeter: number, maxPx = 120): { meters: number; px: number; label: string } {
  const fits = NICE_METERS.filter((m) => m * pxPerMeter <= maxPx);
  const meters = fits.length > 0 ? fits[fits.length - 1]! : NICE_METERS[0];
  return { meters, px: Math.round(meters * pxPerMeter), label: distanceText(meters) };
}

/** O marcador mais perto do toque, até `radius` pixels (ou nenhum). */
export function pickMarker(markers: readonly MapMarker[], sx: number, sy: number, toScreen: (x: number, z: number) => { x: number; y: number }, radius = 14): MapMarker | undefined {
  let best: MapMarker | undefined;
  let bestD = radius;
  for (const m of markers) {
    const p = toScreen(m.x, m.z);
    const d = Math.hypot(p.x - sx, p.y - sy);
    if (d <= bestD) {
      best = m;
      bestD = d;
    }
  }
  return best;
}

/** Legenda (F82): cada símbolo do mapa e o que é, na ordem do mockup (você, grupo, pings, chefes, lugares). */
export const MAP_LEGEND: readonly { symbol: string; label: string }[] = [
  { symbol: "▲", label: "Você (com o rumo)" },
  { symbol: GLYPHS.hexagono.char, label: "Aliado, com o número do slot (✕ caído)" },
  ...PING_KINDS.map((k) => ({ symbol: PING_LABELS[k].symbol, label: `Ping: ${PING_LABELS[k].label} (na cor de quem pingou)` })),
  { symbol: GLYPHS.coroa.char, label: "Chefe" },
  { symbol: "✦", label: "Santuário de Guardião" },
  { symbol: "♜", label: structureName("carranca") },
  { symbol: "◈", label: structureName("pedra-encanto") },
  { symbol: "⌂", label: structureName(MASCATE_ITEM) },
  { symbol: "⛵", label: "Barco" },
  { symbol: "⚰", label: structureName("cruz") },
  { symbol: "@", label: "Redemoinho" },
  { symbol: "◯", label: "Cerco (tracejado: o próximo)" },
];
